import { FabricObject } from 'fabric';
import { ObjectTypeEnum } from '../../_enums/object-type.enum';
import { sanitizeBoundObjectForSave } from '../shapes/mnemonic-binding.runtime.util';
import { buildMnemonicImageUrl, stripAuthTokenFromUrl } from './fabric-image-url.builder';
import { MnemonicCanvasSaveOptions } from './fabric-canvas.types';

function finalizeMnemonicObjectForSave(item: Record<string, unknown>, currentObj?: any): Record<string, unknown> {
    return sanitizeBoundObjectForSave(item, currentObj);
}

function findCanvasObjectForSerializedItem(item: Record<string, unknown>, canvasObjects: FabricObject[], index: number): any {
    if (item.id) {
        const matched = canvasObjects.find((object: any) => object.id === item.id);
        if (matched) {
            return matched;
        }
    }

    return canvasObjects[index] ?? null;
}

function getImageSrc(object: any): string | undefined {
    if (typeof object?.getSrc === 'function') {
        return object.getSrc();
    }

    return object?.src;
}

export function resolveMnemonicImageId(object: Record<string, unknown> | any): string | undefined {
    return object?.imageFileId ?? object?.id;
}

function resolveImageSrcForSave(currentObj: any, mnemonicImages?: Array<{ id: string; fileName: string }>): string | undefined {
    const imageId = resolveMnemonicImageId(currentObj);
    const mnemonicImage = mnemonicImages?.find((image) => image.id === imageId);

    if (mnemonicImage?.fileName) {
        return buildMnemonicImageUrl(mnemonicImage.fileName);
    }

    const src = getImageSrc(currentObj);

    if (typeof src === 'string' && src.includes('/api/virtual-devices/mnemonic-file/')) {
        return stripAuthTokenFromUrl(src);
    }

    return src;
}

export function isSerializedImageType(item: Record<string, unknown>, currentObj?: any): boolean {
    const fabricType = String(item.type ?? currentObj?.type ?? '').toLowerCase();
    const objectType = item.objectType ?? currentObj?.objectType;

    return fabricType === ObjectTypeEnum.image || objectType === ObjectTypeEnum.image || objectType === ObjectTypeEnum.defaultImage;
}

function enrichMnemonicBackgroundImageForSave(
    serializedBackground: Record<string, unknown> | undefined,
    backgroundImage: FabricObject | null | undefined,
    mnemonicImages?: Array<{ id: string; fileName: string }>,
): Record<string, unknown> | undefined {
    if (!serializedBackground && !backgroundImage) {
        return undefined;
    }

    const currentObj = backgroundImage as any;
    const base = serializedBackground ?? { type: ObjectTypeEnum.image };

    if (!currentObj) {
        return serializedBackground;
    }

    return {
        ...base,
        type: ObjectTypeEnum.image,
        id: resolveMnemonicImageId(currentObj),
        imageFileId: resolveMnemonicImageId(currentObj),
        src: resolveImageSrcForSave(currentObj, mnemonicImages),
        objectType: currentObj.objectType ?? ObjectTypeEnum.image,
        name: currentObj.name,
        text: currentObj.text,
        tooltip: currentObj.tooltip,
    };
}

function isGaugeObject(item: Record<string, unknown>, currentObj: any): boolean {
    return currentObj?.objectType === ObjectTypeEnum.gauge || item.type === ObjectTypeEnum.gauge;
}

function isChartObject(item: Record<string, unknown>, currentObj: any): boolean {
    return currentObj?.objectType === ObjectTypeEnum.chart || item.type === ObjectTypeEnum.chart;
}

export function enrichMnemonicObjectsForSave(
    items: Record<string, unknown>[],
    canvasObjects: FabricObject[],
    mnemonicImages?: Array<{ id: string; fileName: string }>,
): Record<string, unknown>[] {
    return items.map((item, index) => {
        const currentObj = findCanvasObjectForSerializedItem(item, canvasObjects, index);

        if (!currentObj) {
            return item;
        }

        if (currentObj.objectType === ObjectTypeEnum.path && item.type === ObjectTypeEnum.group) {
            return finalizeMnemonicObjectForSave(
                {
                    ...item,
                    src: currentObj.src,
                    deviceId: currentObj.deviceId,
                    variableId: currentObj.variableId,
                    selectOption: currentObj.selectOption,
                    objectType: currentObj.objectType,
                },
                currentObj,
            );
        }

        if (isGaugeObject(item, currentObj)) {
            return finalizeMnemonicObjectForSave(
                {
                    ...item,
                    type: ObjectTypeEnum.gauge,
                    id: currentObj.id,
                    objectType: ObjectTypeEnum.gauge,
                    deviceId: currentObj.deviceId,
                    variableId: currentObj.variableId,
                    selectOption: currentObj.selectOption,
                    gauge: currentObj.gauge,
                },
                currentObj,
            );
        }

        if (isChartObject(item, currentObj)) {
            return finalizeMnemonicObjectForSave(
                {
                    ...item,
                    type: ObjectTypeEnum.chart,
                    id: currentObj.id,
                    objectType: ObjectTypeEnum.chart,
                    deviceId: currentObj.deviceId,
                    variableId: currentObj.variableId,
                    selectOption: currentObj.selectOption,
                    chart: currentObj.chart,
                },
                currentObj,
            );
        }

        if (isSerializedImageType(item, currentObj)) {
            const imageId = resolveMnemonicImageId(currentObj);
            const mnemonicImage = mnemonicImages?.find((image) => image.id === imageId);

            return {
                ...item,
                type: ObjectTypeEnum.image,
                id: imageId,
                imageFileId: imageId,
                src: resolveImageSrcForSave(currentObj, mnemonicImages),
                objectType: mnemonicImage ? ObjectTypeEnum.image : currentObj.objectType,
                name: currentObj.name,
                text: currentObj.text,
                tooltip: currentObj.tooltip,
            };
        }

        if (currentObj.objectType === ObjectTypeEnum.group) {
            return {
                ...item,
                objectType: currentObj.objectType,
                name: currentObj.name,
                text: currentObj.text,
                tooltip: currentObj.tooltip,
            };
        }

        if (!currentObj.objectType) {
            return item;
        }

        return finalizeMnemonicObjectForSave(
            {
                ...item,
                id: currentObj.id,
                objectType: currentObj.objectType,
                deviceId: currentObj.deviceId,
                variableId: currentObj.variableId,
                selectOption: currentObj.selectOption,
                text: currentObj.text,
                isStatusColor: currentObj.isStatusColor,
            },
            currentObj,
        );
    });
}

export function enrichMnemonicCanvasForSave(
    json: Record<string, unknown>,
    canvasObjects: FabricObject[],
    backgroundImage: FabricObject | null | undefined,
    options: MnemonicCanvasSaveOptions = {},
): Record<string, unknown> {
    return {
        ...json,
        objects: enrichMnemonicObjectsForSave((json.objects as Record<string, unknown>[]) ?? [], canvasObjects, options.mnemonicImages),
        backgroundImage: enrichMnemonicBackgroundImageForSave(json.backgroundImage as Record<string, unknown> | undefined, backgroundImage, options.mnemonicImages),
    };
}
